// @flow
import { Record, Map, Set, OrderedMap } from 'immutable';
import { type ActionType } from 'redux-actions';
import { receiveAuth } from './authActions';
import normalizeModerationPaths from './normalizeModerationPaths';
import Cookie from 'js-cookie';

type AccessShape = UserAccess;
type RoleShape = UserRole;

const defaultAccess: AccessShape = {
  app: false,
  moderate: false,
  secondOpinion: false,
  search: false,
  channels: false,
  reviews: false,
  qa: false,
  socialcollection: false,
};

const defaultRole: RoleShape = {
  queueModerator: false,
  reviewModerator: false,
  qaModerator: false,
  moderationAdmin: false,
  brandEngage: false,
  admin: false,
  internal: false,
};

const RoleRecord = Record(defaultRole);
const AccessRecord = Record(defaultAccess);

const SOCIAL_ANSWERS_PROGRAMS = [
  'AdminQuestionQueue', // QuestionQueueController
  'AnswerBoxModeratorLevelTwo', // ProductAnswersModerator
  'AnswerBoxModerator2LevelTwo', // ModeratorCommon?
  'ExpertUserQuestionQueue', // ProductAnswersModerator
];

const socialCollectionHasAccessPath = '/socialcollection';

function createRoleFromProgramGroups(programGroups: {
  [string]: string,
}): RoleRecord {
  const set = new Set(Object.keys(programGroups));

  const queueModerator = set.has('PowerReviewsModerator');
  const reviewModerator = set.has('ReviewModeratorLevelTwo');
  const qaModerator = SOCIAL_ANSWERS_PROGRAMS.reduce(
    (flag, programName) => flag || set.has(programName),
    false
  );
  const brandEngage = set.has('BrandEngage');
  const moderationAdmin = set.has('PowerReviewsModerationAdmin');
  const admin = moderationAdmin && reviewModerator && qaModerator;
  const internal = queueModerator || moderationAdmin;

  return new RoleRecord({
    queueModerator,
    reviewModerator,
    qaModerator,
    moderationAdmin,
    admin,
    internal,
    brandEngage,
  });
}

function createAccessFromRole(
  apps: Map<string, string>,
  role: RoleRecord
): AccessRecord {
  const reviews = apps.has('Reviews');
  const qa = apps.has('QA');
  const moderate = apps.has('Moderation');
  const app = reviews || qa || moderate;
  const secondOpinion = role.moderationAdmin;
  const search = role.reviewModerator || role.qaModerator;
  const channels = role.reviewModerator || role.qaModerator || role.brandEngage;
  const socialcollection =
    apps.get('SocialCollection') === socialCollectionHasAccessPath;

  const props: AccessShape = {
    app,
    moderate,
    secondOpinion,
    search,
    channels,
    reviews,
    qa,
    socialcollection,
  };

  return new AccessRecord(props);
}

type AuthRecordShape = {
  requestStatus: ?RequestStatus,
  name: string,
  email: string,
  token: ?string,
  profile_id: ?number,
  loggedIn: boolean,
  programGroups: Map<string, string>,
  access: typeof AccessRecord,
  role: typeof RoleRecord,
  apps: Map<string, string>,
};

const defaultAuthRecord: AuthRecordShape = {
  requestStatus: null,
  name: '',
  email: '',
  token: '',
  profile_id: null,
  loggedIn: false,
  access: new AccessRecord(),
  role: new RoleRecord(),
  programGroups: Map(),
  apps: OrderedMap(),
};

type AuthPayload = $PropertyType<ActionType<typeof receiveAuth>, 'payload'>;

export default class AuthState extends Record(defaultAuthRecord) {
  static getUserInfo(state: AuthState): UserInfo {
    return {
      username: state.name,
      email: state.email,
      role: state.role,
      access: state.access,
    };
  }

  needsLogin(): boolean {
    return !this.isLoggedIn() && !this.hasToken();
  }

  needsTokenRefresh(): boolean {
    return !this.isLoggedIn() && this.hasToken();
  }

  isLoggedIn(): boolean {
    return this.loggedIn;
  }

  hasToken(): boolean {
    return Boolean(this.token);
  }

  getUserName(): string {
    return this.name;
  }

  getToken(): string {
    return this.token;
  }

  isPowerReviewsModerator(): boolean {
    return this.programGroups.has('PowerReviewsModerator');
  }

  isStandardModerator(): boolean {
    return (
      this.isPowerReviewsModerator() &&
      this.isAdmin() === false &&
      this.isBrandEngageClient() === false
    );
  }

  isQAEnabled(): boolean {
    return SOCIAL_ANSWERS_PROGRAMS.reduce(
      (flag, programName) => flag || this.programGroups.has(programName),
      false
    );
  }

  isBrandEngageClient(): boolean {
    return this.programGroups.has('BrandEngage');
  }

  isInternal(): boolean {
    return this.isAdmin() || this.isPowerReviewsModerator();
  }

  isAdmin(): boolean {
    return this.programGroups.has('PowerReviewsModerationAdmin');
  }

  isRunAsMode(): boolean {
    return Boolean(Cookie.get('run_as'));
  }

  static createFromPayload(payload: AuthPayload) {
    const {
      program_groups,
      name,
      email,
      apps: _apps,
      token,
      profile_id,
    } = payload;
    const apps = OrderedMap(
      normalizeModerationPaths(_apps).map(({ label, href }) => [label, href])
    );

    return new AuthState().withMutations(s => {
      const programGroups = Map(program_groups);
      const role = createRoleFromProgramGroups(program_groups);
      const access = createAccessFromRole(apps, role);

      s.merge({
        name,
        apps,
        email,
        token,
        programGroups,
        requestStatus: 'complete',
        loggedIn: true,
        role,
        access,
        profile_id,
      });
    });
  }

  static createWithExistingToken(token: ?string): AuthState {
    return new AuthState({ token });
  }
}



// WEBPACK FOOTER //
// ./src/ducks/AuthState.js